/**
 * Lead Detail — the most important LCC demo surface.
 *
 * The workflow is deliberately simple: header (who/what + verified check +
 * due state), top-level actions (Call / Email / Edit), one "Next Step" card
 * (Record Interaction or AI Assist), then Overview / Timeline / Contact /
 * Organization / Submission / AI & Approvals tabs. Everything writes to the
 * Supabase demo project via the dedicated Worker; nothing outbound is sent.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Building2,
  CalendarClock,
  ClipboardList,
  History,
  Loader2,
  Mail,
  Pencil,
  Sparkles,
  User,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  lccApi,
  type LccAssistAction,
  type LccAssistResult,
  type LccLead,
  type LccLeadDetail,
} from '@/lib/lcc-api';
import {
  formatLccDateTime,
  formatLccLabel,
  formatLccRelative,
  leadDueState,
  LccErrorCard,
  LccLoadingCard,
  LccStatusBadge,
  nextActionForLead,
} from './lcc-shared';
import { CallButton, EmailComposeDialog, useLccInvalidate } from './lead-actions';
import {
  RecordInteractionDialog,
  RecordInteractionForm,
  VerifiedCheck,
  type RecordInteractionDefaults,
} from './record-interaction';
import { ApprovalPendingBadge } from './approval-actions';
import { EditLeadDialog } from './lead-form-dialog';
import { EditContactDialog, EditOrganizationDialog } from './record-dialogs';

export function LeadDetailView() {
  const { leadId = '' } = useParams();
  const { data, isLoading, error } = useQuery({
    queryKey: ['lcc', 'lead-detail', leadId],
    queryFn: () => lccApi.leadDetail(leadId),
    enabled: leadId.length > 0,
  });

  if (isLoading) return <LccLoadingCard />;
  if (error != null || !data) {
    return (
      <div className="mx-auto w-full max-w-[1100px]">
        <LccErrorCard title="Couldn't load this lead" error={error ?? new Error('Lead not found')} />
        <Button asChild variant="outline" size="sm" className="mt-3 gap-1.5">
          <Link to="/demo/lcc/loop">
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
            Back to LeadLoop
          </Link>
        </Button>
      </div>
    );
  }
  return <LeadDetailLoaded detail={data} />;
}

function LeadDetailLoaded({ detail }: { detail: LccLeadDetail }) {
  const { lead } = detail;
  const [emailOpen, setEmailOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  /** Set after logging a call/email so the result gets recorded right away. */
  const [followUpDefaults, setFollowUpDefaults] = useState<RecordInteractionDefaults | null>(null);

  const dueState = leadDueState(lead);
  const hasPendingApproval = detail.approvals.some((a) => a.status === 'pending');

  return (
    <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-4">
      {/* ---------- header ---------- */}
      <div className="flex flex-col gap-2">
        <Link
          to="/demo/lcc/loop"
          className="inline-flex items-center gap-1 text-[12px] text-muted-foreground underline-offset-2 hover:underline"
        >
          <ArrowLeft className="h-3 w-3" aria-hidden />
          LeadLoop
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-1.5">
            <div className="flex items-center gap-2">
              <VerifiedCheck lead={lead} />
              <h1 className="text-[20px] font-semibold leading-tight">{lead.title}</h1>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px] text-muted-foreground">
              {(detail.organization || lead.companyName) && (
                <span className="inline-flex items-center gap-1">
                  <Building2 className="h-3.5 w-3.5" aria-hidden />
                  {detail.organization ? (
                    <Link
                      to={`/demo/lcc/organizations/${detail.organization.id}`}
                      className="underline-offset-2 hover:underline"
                    >
                      {detail.organization.name}
                    </Link>
                  ) : (
                    lead.companyName
                  )}
                </span>
              )}
              {(detail.contact || lead.contactName) && (
                <span className="inline-flex items-center gap-1">
                  <User className="h-3.5 w-3.5" aria-hidden />
                  {detail.contact ? (
                    <Link
                      to={`/demo/lcc/contacts/${detail.contact.id}`}
                      className="underline-offset-2 hover:underline"
                    >
                      {detail.contact.fullName}
                    </Link>
                  ) : (
                    lead.contactName
                  )}
                </span>
              )}
              <span>{formatLccLabel(lead.source)}</span>
              {lead.campaign && <span>{lead.campaign}</span>}
              <span title={lead.createdAt}>Created {formatLccRelative(lead.createdAt)}</span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <LccStatusBadge status={lead.status} label={lead.statusLabel} />
              {hasPendingApproval && <ApprovalPendingBadge />}
              {dueState.kind !== 'closed' && (
                <span className="inline-flex items-center gap-1 text-[12px] text-muted-foreground">
                  <CalendarClock className="h-3.5 w-3.5" aria-hidden />
                  {dueState.label}
                </span>
              )}
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap gap-1.5">
            <CallButton
              lead={lead}
              label="Call Contact"
              onLogged={() => setFollowUpDefaults({ interactionType: 'call' })}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="gap-1.5"
              disabled={!lead.email}
              title={lead.email ? undefined : 'No email on file'}
              onClick={() => setEmailOpen(true)}
            >
              <Mail className="h-3.5 w-3.5" aria-hidden />
              Send Email
            </Button>
            <Button type="button" size="sm" className="gap-1.5" onClick={() => setEditOpen(true)}>
              <Pencil className="h-3.5 w-3.5" aria-hidden />
              Edit Lead
            </Button>
          </div>
        </div>
      </div>

      {/* ---------- Next Step (the one workflow card) ---------- */}
      <NextStepCard lead={lead} />

      {/* ---------- tabs ---------- */}
      <LeadDetailTabs detail={detail} />

      <EmailComposeDialog
        lead={lead}
        open={emailOpen}
        onOpenChange={setEmailOpen}
        onLogged={() => setFollowUpDefaults({ interactionType: 'email' })}
      />
      <EditLeadDialog lead={lead} open={editOpen} onOpenChange={setEditOpen} />
      <RecordInteractionDialog
        lead={lead}
        open={followUpDefaults !== null}
        onOpenChange={(open) => {
          if (!open) setFollowUpDefaults(null);
        }}
        title="Record what happened"
        defaults={followUpDefaults ?? undefined}
      />
    </div>
  );
}

// ---------- Next Step card (Record Interaction / AI Assist) ----------

function NextStepCard({ lead }: { lead: LccLead }) {
  const [mode, setMode] = useState<'idle' | 'record' | 'assist'>('idle');
  const dueState = leadDueState(lead);

  return (
    <Card className="p-3 sm:p-4">
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.8px] text-muted-foreground">
        Next Step
      </div>

      {lead.nextStep ? (
        <div className="mb-3 rounded-md border border-primary/25 bg-primary/5 px-3 py-2 text-[13px]">
          <span className="font-medium text-primary">Next:</span> {lead.nextStep}
          {dueState.kind !== 'closed' && (
            <span className="text-muted-foreground"> · {dueState.label}</span>
          )}
          {lead.nextStepUpdatedBy && (
            <div className="mt-0.5 text-[11px] text-muted-foreground">
              Set by {lead.nextStepUpdatedBy} {formatLccRelative(lead.nextStepUpdatedAt)}
            </div>
          )}
        </div>
      ) : (
        <p className="mb-3 text-[12.5px] text-muted-foreground">
          {dueState.kind === 'closed'
            ? 'This lead is closed — no next step needed.'
            : 'No next step set yet — record an interaction to set one.'}
        </p>
      )}

      <div className="grid gap-2 sm:grid-cols-2">
        <NextStepTile
          active={mode === 'record'}
          icon={<ClipboardList className="h-4 w-4" aria-hidden />}
          title="Record Interaction"
          description="Log something that already happened — a call, text, email, or new info — and set what happens next."
          onClick={() => setMode((m) => (m === 'record' ? 'idle' : 'record'))}
        />
        <NextStepTile
          active={mode === 'assist'}
          icon={<Sparkles className="h-4 w-4" aria-hidden />}
          title="AI Assist"
          description="Generate the next action — drafts and suggestions route through the approval queue, nothing is sent."
          onClick={() => setMode((m) => (m === 'assist' ? 'idle' : 'assist'))}
        />
      </div>

      {mode === 'record' && (
        <div className="mt-3 border-t border-border/60 pt-3">
          <RecordInteractionForm lead={lead} onDone={() => setMode('idle')} />
        </div>
      )}
      {mode === 'assist' && (
        <div className="mt-3 border-t border-border/60 pt-3">
          <AiAssistPanel lead={lead} />
        </div>
      )}
    </Card>
  );
}

function NextStepTile({
  active,
  icon,
  title,
  description,
  onClick,
}: {
  active: boolean;
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-start gap-2.5 rounded-lg border p-3 text-left transition-colors ${
        active
          ? 'border-primary bg-primary/5'
          : 'border-border bg-background hover:border-primary/40 hover:bg-muted/40'
      }`}
    >
      <span
        className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${
          active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
        }`}
      >
        {icon}
      </span>
      <span className="min-w-0">
        <span className="block text-[13.5px] font-semibold leading-tight">{title}</span>
        <span className="mt-0.5 block text-[12px] leading-snug text-muted-foreground">
          {description}
        </span>
      </span>
    </button>
  );
}

// ---------- AI Assist panel (simulated, approval-routed) ----------

const ASSIST_ACTIONS: Array<{ action: LccAssistAction; label: string }> = [
  { action: 'follow_up_email', label: 'Generate Follow-Up Email' },
  { action: 'request_info', label: 'Request Additional Information' },
  { action: 'summarize', label: 'Summarize Lead' },
  { action: 'suggest_next_step', label: 'Suggest Next Step' },
  { action: 'check_duplicate', label: 'Check for Duplicate' },
];

function AiAssistPanel({ lead }: { lead: LccLead }) {
  const invalidate = useLccInvalidate();
  const [result, setResult] = useState<LccAssistResult | null>(null);

  const mutation = useMutation({
    mutationFn: (action: LccAssistAction) => lccApi.assist(lead.id, action),
    onSuccess: (data) => {
      setResult(data);
      invalidate();
    },
  });

  return (
    <div className="space-y-2.5">
      <div className="flex flex-wrap gap-1.5">
        {ASSIST_ACTIONS.map(({ action, label }) => (
          <Button
            key={action}
            type="button"
            size="sm"
            variant="outline"
            className="h-8 gap-1.5"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate(action)}
          >
            {mutation.isPending && mutation.variables === action ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
              <Sparkles className="h-3.5 w-3.5" aria-hidden />
            )}
            {label}
          </Button>
        ))}
      </div>
      <p className="text-[11.5px] text-muted-foreground">
        All AI actions are simulated for the demo — email drafts go to the approval queue for your
        review; nothing is ever sent automatically.
      </p>
      {mutation.isError && (
        <p className="text-[12px] text-destructive">
          {mutation.error instanceof Error ? mutation.error.message : 'AI Assist failed.'}
        </p>
      )}
      {result && (
        <div className="rounded-md border border-border bg-background px-3 py-2 text-[12.5px]">
          {result.kind === 'approval' && result.approval && (
            <span>
              <span className="font-medium">Draft queued for approval:</span>{' '}
              {result.approval.title} —{' '}
              <Link to="/demo/lcc/loop" className="underline underline-offset-2">
                review it in LeadLoop
              </Link>
            </span>
          )}
          {result.kind === 'summary' && (
            <span>
              <span className="font-medium">AI summary updated:</span>{' '}
              {result.event?.description ?? result.lead?.aiSummary}
            </span>
          )}
          {result.kind === 'event' && result.event && (
            <span>
              <span className="font-medium">{result.event.title}</span>
              {result.event.description ? ` — ${result.event.description}` : ''}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- tabs ----------

function LeadDetailTabs({ detail }: { detail: LccLeadDetail }) {
  const { lead } = detail;
  const [showRawSubmission, setShowRawSubmission] = useState(false);
  const [editOrgOpen, setEditOrgOpen] = useState(false);
  const [editContactOpen, setEditContactOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  const eventsQuery = useQuery({
    queryKey: ['lcc', 'lead-events', lead.id],
    queryFn: () => lccApi.leadEvents(lead.id),
  });
  const orgQuery = useQuery({
    queryKey: ['lcc', 'org-detail', lead.organizationId],
    queryFn: () => lccApi.organizationDetail(lead.organizationId ?? ''),
    enabled: Boolean(lead.organizationId),
  });
  const contactQuery = useQuery({
    queryKey: ['lcc', 'contact-detail', lead.contactId],
    queryFn: () => lccApi.contactDetail(lead.contactId ?? ''),
    enabled: Boolean(lead.contactId),
  });

  const pendingApproval = detail.approvals.find((a) => a.status === 'pending');
  const dueState = leadDueState(lead);

  return (
    <Tabs defaultValue="overview">
      <TabsList className="flex-wrap">
        <TabsTrigger value="overview">Overview</TabsTrigger>
        <TabsTrigger value="timeline">Timeline</TabsTrigger>
        <TabsTrigger value="contact">Contact</TabsTrigger>
        <TabsTrigger value="organization">Organization</TabsTrigger>
        <TabsTrigger value="submission">Submission</TabsTrigger>
        <TabsTrigger value="ai">AI & Approvals</TabsTrigger>
      </TabsList>

      {/* ----- Overview ----- */}
      <TabsContent value="overview" className="mt-3 space-y-3">
        <div className="grid gap-3 lg:grid-cols-2">
          <Card className="space-y-2 p-4">
            <SectionLabel>Lead message</SectionLabel>
            <p className="whitespace-pre-wrap text-[13px] leading-relaxed">
              {lead.message ?? 'No message captured.'}
            </p>
            {lead.aiSummary && (
              <>
                <SectionLabel>AI summary</SectionLabel>
                <p className="text-[13px] leading-relaxed text-muted-foreground">{lead.aiSummary}</p>
              </>
            )}
            <div className="rounded-md border border-primary/25 bg-primary/5 px-3 py-2 text-[13px]">
              <span className="font-medium text-primary">Recommended next action:</span>{' '}
              {nextActionForLead(lead)}
            </div>
            {pendingApproval && (
              <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-[12.5px]">
                <ClipboardList className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                <span>
                  Waiting on approval: <span className="font-medium">{pendingApproval.title}</span>{' '}
                  — <Link to="/demo/lcc/loop" className="underline underline-offset-2">review it in LeadLoop</Link>
                </span>
              </div>
            )}
          </Card>
          <Card className="space-y-3 p-4">
            <SectionLabel>Next step</SectionLabel>
            {dueState.kind === 'closed' ? (
              <p className="text-[12.5px] text-muted-foreground">This lead is closed.</p>
            ) : (
              <div className="space-y-1 text-[12.5px]">
                <p>{lead.nextStep ?? 'Not set yet — record an interaction to set one.'}</p>
                <p className="text-muted-foreground">
                  {dueState.label}
                  {lead.nextFollowUpAt ? ` · ${formatLccDateTime(lead.nextFollowUpAt)}` : ''}
                </p>
                {lead.nextStepUpdatedBy && (
                  <p className="text-[11px] text-muted-foreground">
                    Set by {lead.nextStepUpdatedBy} {formatLccRelative(lead.nextStepUpdatedAt)}
                  </p>
                )}
              </div>
            )}
            <SectionLabel>Details</SectionLabel>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-[12.5px]">
              <DetailItem label="Contact" value={lead.contactName} />
              <DetailItem label="Company" value={detail.organization?.name ?? lead.companyName} />
              <DetailItem label="Email" value={lead.email} />
              <DetailItem label="Phone" value={lead.phone} />
              <DetailItem label="Source" value={formatLccLabel(lead.source)} />
              <DetailItem label="Source detail" value={lead.sourceDetail} />
              <DetailItem label="Campaign" value={lead.campaign} />
              <DetailItem label="Assigned to" value={lead.assignedTo ?? 'Unassigned'} />
              <DetailItem label="Created" value={formatLccDateTime(lead.createdAt)} />
              <DetailItem label="Last activity" value={formatLccDateTime(lead.lastActivityAt)} />
              <DetailItem label="Returns to LeadLoop" value={formatLccDateTime(lead.nextFollowUpAt)} />
              <DetailItem label="Website" value={lead.website} />
            </dl>
          </Card>
        </div>
      </TabsContent>

      {/* ----- Timeline ----- */}
      <TabsContent value="timeline" className="mt-3">
        <Card className="p-4">
          {eventsQuery.isLoading && (
            <p className="text-[12.5px] text-muted-foreground">Loading timeline…</p>
          )}
          {eventsQuery.error != null && (
            <p className="text-[12.5px] text-destructive">Couldn't load the timeline.</p>
          )}
          {(eventsQuery.data?.items ?? []).length === 0 && !eventsQuery.isLoading && (
            <p className="text-[12.5px] text-muted-foreground">No events yet.</p>
          )}
          <ol className="space-y-3">
            {(eventsQuery.data?.items ?? []).map((event) => (
              <li key={event.id} className="flex gap-3">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted">
                  <History className="h-3 w-3 text-muted-foreground" aria-hidden />
                </span>
                <div className="min-w-0">
                  <div className="text-[13px] font-medium leading-tight">{event.title}</div>
                  {event.description && (
                    <div className="mt-0.5 whitespace-pre-wrap text-[12px] text-muted-foreground">
                      {event.description}
                    </div>
                  )}
                  {typeof event.metadata.nextStepTo === 'string' && (
                    <div className="mt-0.5 text-[12px] text-muted-foreground">
                      Next step → {event.metadata.nextStepTo}
                    </div>
                  )}
                  <div className="mt-0.5 text-[11px] text-muted-foreground">
                    {formatLccLabel(event.eventType)} · {event.actorName ?? event.actorType} ·{' '}
                    <span title={event.createdAt}>{formatLccDateTime(event.createdAt)}</span>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </Card>
      </TabsContent>

      {/* ----- Contact ----- */}
      <TabsContent value="contact" className="mt-3">
        <Card className="space-y-3 p-4">
          {detail.contact ? (
            <>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="text-[15px] font-semibold">{detail.contact.fullName}</div>
                  <div className="text-[12.5px] text-muted-foreground">
                    {detail.contact.roleTitle ?? 'Contact'}
                    {detail.contact.companyName ? ` · ${detail.contact.companyName}` : ''}
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="gap-1.5"
                    disabled={!(detail.contact.email ?? lead.email)}
                    onClick={() => setEmailOpen(true)}
                  >
                    <Mail className="h-3.5 w-3.5" aria-hidden />
                    Send Email
                  </Button>
                  <CallButton lead={lead} />
                  <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={() => setEditContactOpen(true)}>
                    <Pencil className="h-3.5 w-3.5" aria-hidden />
                    Edit Contact
                  </Button>
                </div>
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-[12.5px]">
                <DetailItem label="Email" value={detail.contact.email} />
                <DetailItem label="Phone" value={detail.contact.phone} />
                <DetailItem label="Type" value={formatLccLabel(detail.contact.type)} />
                <DetailItem
                  label="Organization"
                  value={detail.organization?.name ?? detail.contact.companyName}
                />
              </dl>
              <SectionLabel>Related leads</SectionLabel>
              <RelatedLeadsList leads={contactQuery.data?.leads ?? []} currentLeadId={lead.id} loading={contactQuery.isLoading} />
              <EditContactDialog contact={detail.contact} open={editContactOpen} onOpenChange={setEditContactOpen} />
            </>
          ) : (
            <>
              <SectionLabel>No linked contact record</SectionLabel>
              <p className="text-[12.5px] text-muted-foreground">
                This lead only has raw contact fields. They're shown below and editable via Edit Lead.
              </p>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-[12.5px]">
                <DetailItem label="Name" value={lead.contactName} />
                <DetailItem label="Email" value={lead.email} />
                <DetailItem label="Phone" value={lead.phone} />
                <DetailItem label="Company" value={lead.companyName} />
              </dl>
            </>
          )}
          <EmailComposeDialog lead={lead} open={emailOpen} onOpenChange={setEmailOpen} />
        </Card>
      </TabsContent>

      {/* ----- Organization ----- */}
      <TabsContent value="organization" className="mt-3">
        <Card className="space-y-3 p-4">
          {detail.organization ? (
            <>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <Link
                    to={`/demo/lcc/organizations/${detail.organization.id}`}
                    className="text-[15px] font-semibold underline-offset-2 hover:underline"
                  >
                    {detail.organization.name}
                  </Link>
                  <div className="text-[12.5px] text-muted-foreground">
                    {detail.organization.industry ?? 'Organization'} · {detail.organization.status}
                  </div>
                </div>
                <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={() => setEditOrgOpen(true)}>
                  <Pencil className="h-3.5 w-3.5" aria-hidden />
                  Edit Organization
                </Button>
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-[12.5px]">
                <DetailItem label="Website" value={detail.organization.website} link />
                <DetailItem label="Phone" value={detail.organization.phone} />
                <DetailItem label="Email" value={detail.organization.email} />
                <DetailItem label="Notes" value={detail.organization.notes} />
              </dl>
              <SectionLabel>Contacts</SectionLabel>
              {orgQuery.isLoading && <p className="text-[12.5px] text-muted-foreground">Loading…</p>}
              <ul className="space-y-1 text-[12.5px]">
                {(orgQuery.data?.contacts ?? []).map((contact) => (
                  <li key={contact.id} className="flex flex-wrap gap-x-3 gap-y-0.5">
                    <Link to={`/demo/lcc/contacts/${contact.id}`} className="font-medium underline-offset-2 hover:underline">
                      {contact.fullName}
                    </Link>
                    <span className="text-muted-foreground">{contact.roleTitle}</span>
                    <span className="text-muted-foreground">{contact.email}</span>
                  </li>
                ))}
                {orgQuery.data && orgQuery.data.contacts.length === 0 && (
                  <li className="text-muted-foreground">No contacts on file.</li>
                )}
              </ul>
              <SectionLabel>Leads for this organization</SectionLabel>
              <RelatedLeadsList leads={orgQuery.data?.leads ?? []} currentLeadId={lead.id} loading={orgQuery.isLoading} />
              <EditOrganizationDialog organization={detail.organization} open={editOrgOpen} onOpenChange={setEditOrgOpen} />
            </>
          ) : (
            <>
              <SectionLabel>No linked organization</SectionLabel>
              <p className="text-[12.5px] text-muted-foreground">
                {lead.companyName
                  ? `The lead mentions "${lead.companyName}" but isn't linked to an organization record yet.`
                  : 'This lead has no business attached.'}
              </p>
            </>
          )}
        </Card>
      </TabsContent>

      {/* ----- Submission ----- */}
      <TabsContent value="submission" className="mt-3">
        <Card className="space-y-3 p-4">
          {detail.submission ? (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="text-[14px] font-semibold">{detail.submission.formName}</div>
                  <div className="text-[12px] text-muted-foreground">
                    {formatLccLabel(detail.submission.source)} ·{' '}
                    {formatLccDateTime(detail.submission.createdAt)}
                  </div>
                </div>
                <Badge variant="muted">{formatLccLabel(detail.submission.status)}</Badge>
              </div>
              <dl className="grid gap-x-4 gap-y-2 text-[12.5px] sm:grid-cols-2">
                {Object.entries(detail.submission.payload).map(([key, value]) => (
                  <DetailItem key={key} label={formatLccLabel(key)} value={String(value)} />
                ))}
              </dl>
              <button
                type="button"
                className="text-[12px] text-muted-foreground underline-offset-2 hover:underline"
                onClick={() => setShowRawSubmission((v) => !v)}
              >
                {showRawSubmission ? 'Hide advanced (raw JSON)' : 'Advanced: show raw JSON'}
              </button>
              {showRawSubmission && (
                <pre className="overflow-x-auto rounded-md border border-border bg-muted/40 p-3 text-[11px] leading-relaxed">
                  {JSON.stringify(detail.submission.payload, null, 2)}
                </pre>
              )}
            </>
          ) : (
            <>
              <SectionLabel>No original submission</SectionLabel>
              <p className="text-[12.5px] text-muted-foreground">
                This lead was logged via {formatLccLabel(lead.source)}
                {lead.sourceDetail ? ` (${lead.sourceDetail})` : ''} — there's no raw form payload to show.
              </p>
            </>
          )}
        </Card>
      </TabsContent>

      {/* ----- AI & Approvals ----- */}
      <TabsContent value="ai" className="mt-3">
        <Card className="space-y-3 p-4">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-[12.5px]">
            <DetailItem
              label="AI score"
              value={typeof lead.aiScore === 'number' ? `${Math.round(lead.aiScore * 100)} / 100` : undefined}
            />
            <DetailItem label="AI verification status" value={formatLccLabel(lead.aiVerificationStatus)} />
            <DetailItem
              label="Verified"
              value={
                lead.verified
                  ? `Yes — via ${formatLccLabel(lead.verifiedMethod ?? 'other')} by ${lead.verifiedBy ?? 'unknown'}`
                  : 'Not yet'
              }
            />
            <DetailItem label="Verified at" value={formatLccDateTime(lead.verifiedAt)} />
          </dl>
          {lead.aiSummary && (
            <>
              <SectionLabel>AI summary</SectionLabel>
              <p className="text-[13px] leading-relaxed">{lead.aiSummary}</p>
            </>
          )}
          <SectionLabel>Approval history</SectionLabel>
          {detail.approvals.length === 0 && (
            <p className="text-[12.5px] text-muted-foreground">
              No AI-proposed actions yet — use AI Assist in the Next Step card.
            </p>
          )}
          <ul className="space-y-2">
            {detail.approvals.map((approval) => (
              <li key={approval.id} className="rounded-md border border-border p-3">
                <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
                  <Badge
                    variant={
                      approval.status === 'approved'
                        ? 'success'
                        : approval.status === 'rejected'
                          ? 'destructive'
                          : approval.status === 'pending'
                            ? 'warning'
                            : 'muted'
                    }
                  >
                    {formatLccLabel(approval.status)}
                  </Badge>
                  <span className="font-medium">{approval.title}</span>
                  <span className="text-muted-foreground">
                    {formatLccDateTime(approval.resolvedAt ?? approval.createdAt)}
                  </span>
                </div>
                {approval.description && (
                  <p className="mt-1 text-[12px] text-muted-foreground">{approval.description}</p>
                )}
              </li>
            ))}
          </ul>
          <SectionLabel>Verification history (simulated)</SectionLabel>
          {detail.verifications.length === 0 && (
            <p className="text-[12.5px] text-muted-foreground">No verification runs yet.</p>
          )}
          <ul className="space-y-2.5">
            {detail.verifications.map((v) => (
              <li key={v.id} className="rounded-md border border-border p-3">
                <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
                  <Badge variant={v.result === 'verified' ? 'success' : v.result === 'failed' ? 'destructive' : 'muted'}>
                    {formatLccLabel(v.result)}
                  </Badge>
                  {typeof v.score === 'number' && (
                    <span className="tabular-nums text-muted-foreground">score {Math.round(v.score * 100)}</span>
                  )}
                  <span className="text-muted-foreground">{formatLccDateTime(v.createdAt)}</span>
                </div>
                {v.summary && <p className="mt-1.5 text-[12.5px]">{v.summary}</p>}
                {v.transcript && (
                  <pre className="mt-2 overflow-x-auto whitespace-pre-wrap rounded-md bg-muted/40 p-2.5 text-[11.5px] leading-relaxed">
                    {v.transcript}
                  </pre>
                )}
              </li>
            ))}
          </ul>
        </Card>
      </TabsContent>
    </Tabs>
  );
}

// ---------- small pieces ----------

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] font-semibold uppercase tracking-[0.7px] text-muted-foreground/80">
      {children}
    </div>
  );
}

function DetailItem({ label, value, link }: { label: string; value?: string | null; link?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] font-semibold uppercase tracking-[0.7px] text-muted-foreground/70">{label}</dt>
      <dd className="break-words text-foreground">
        {value ? (
          link ? (
            <a href={value} target="_blank" rel="noreferrer" className="underline-offset-2 hover:underline">
              {value}
            </a>
          ) : (
            value
          )
        ) : (
          '—'
        )}
      </dd>
    </div>
  );
}

function RelatedLeadsList({
  leads,
  currentLeadId,
  loading,
}: {
  leads: LccLead[];
  currentLeadId: string;
  loading: boolean;
}) {
  if (loading) return <p className="text-[12.5px] text-muted-foreground">Loading…</p>;
  const related = leads.filter((l) => l.id !== currentLeadId);
  if (related.length === 0) {
    return <p className="text-[12.5px] text-muted-foreground">No other leads.</p>;
  }
  return (
    <ul className="space-y-1 text-[12.5px]">
      {related.map((l) => (
        <li key={l.id} className="flex flex-wrap items-center gap-2">
          <Link to={`/demo/lcc/leads/${l.id}`} className="font-medium underline-offset-2 hover:underline">
            {l.title}
          </Link>
          <LccStatusBadge status={l.status} label={l.statusLabel} />
          <span className="text-muted-foreground">{formatLccRelative(l.createdAt)}</span>
        </li>
      ))}
    </ul>
  );
}
